import React, { useEffect, useState } from 'react';
import PageBreadcrumb from '../../components/common/PageBreadCrumb';
import ComponentCard from '../../components/common/ComponentCard';
import PageMeta from '../../components/common/PageMeta';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '../../components/ui/table';
import dayjs from 'dayjs';
import { Payment, usePaymentStore } from '../../store/usePaymentStore';
import { PaymentType, usePaymentTypeStore } from '../../store/usePaymentTypeStore';
import { FamilyMember, useFamilyMemberStore } from '../../store/useFamilyMemberStore';
import { Family, useFamilyStore } from '../../store/useFamilyStore';
import { usePaymentObligationStore } from '../../store/usePaymentObligationStore';
import { Button, Label, Modal, Popover, TextInput, Select } from 'flowbite-react';
import { HiOutlineExclamationCircle } from 'react-icons/hi';
import toast from 'react-hot-toast';
import { useCanAccessResource, useHasPermission, useIsMainAdmin } from '../../utils/permissions';
import { usePermissionStore } from '../../store/usePermissionStore';
import AccessDenied from '../../components/common/AccessDenied';

export default function PaymentsPage() {
  const getPayments = usePaymentStore((state) => state.getPayments);
  const deletePayment = usePaymentStore((state) => state.deletePayment);
  const payments = usePaymentStore((state) => state.payments);
  const createPayment = usePaymentStore((state) => state.createPayment);
  const updatePayment = usePaymentStore((state) => state.updatePayment);
  const exportPayments = usePaymentStore((state) => state.exportPayments);
  const loading = usePaymentStore((state) => state.loading);
  const pagination = usePaymentStore((state) => state.pagination);

  const getPaymentTypes = usePaymentTypeStore((state) => state.getPaymentTypes);
  const paymentTypes = usePaymentTypeStore((state) => state.paymentTypes);

  const getFamilies = useFamilyStore((state) => state.getFamilies);
  const families = useFamilyStore((state) => state.families);

  const getFamilyMembers = useFamilyMemberStore((state) => state.getFamilyMembers);
  const familyMembers = useFamilyMemberStore((state) => state.members);

  const getPaymentObligations = usePaymentObligationStore((state) => state.getPaymentObligations);
  const generateObligations = usePaymentObligationStore((state) => state.generateObligations);
  const createPaymentObligation = usePaymentObligationStore((state) => state.createPaymentObligation);
  const obligations = usePaymentObligationStore((state) => state.obligations);

  const canAccess = useCanAccessResource('payments');
  const hasReadPermission = useHasPermission('payments', 'read');
  const hasCreatePermission = useHasPermission('payments', 'create');
  const hasUpdatePermission = useHasPermission('payments', 'update');
  const hasDeletePermission = useHasPermission('payments', 'delete');
  const isMainAdmin = useIsMainAdmin();
  const isLoadingPermissions = usePermissionStore((state) => state.isLoading);
  const hasFetched = usePermissionStore((state) => state.hasFetched);

  const [tableData, setTableData] = useState<Payment[]>([]);
  const [search, setSearch] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [limit] = useState<number>(10);
  const [openModal, setOpenModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<string | null>(null);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [modalType, setModalType] = useState<string>('add');
  const [editId, setEditId] = useState<string | null>(null);

  // Filters
  const [filterPaymentTypeId, setFilterPaymentTypeId] = useState<string>('');
  const [filterFamilyId, setFilterFamilyId] = useState<string>('');
  const [filterMemberId, setFilterMemberId] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterStartDate, setFilterStartDate] = useState<string>('');
  const [filterEndDate, setFilterEndDate] = useState<string>('');

  // Form fields
  const [paymentTypeId, setPaymentTypeId] = useState<string>('');
  const [familyId, setFamilyId] = useState<string>('');
  const [memberId, setMemberId] = useState<string>('');
  const [totalAmountPaid, setTotalAmountPaid] = useState<number>(0);
  const [paymentAllocations, setPaymentAllocations] = useState<Array<{ paymentObligationId: string; amountApplied: number }>>([]);
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'upi' | 'card' | 'bankTransfer'>('cash');
  const [transactionId, setTransactionId] = useState<string>('');
  const [status, setStatus] = useState<'pending' | 'success' | 'failed'>('success');
  const [paidAt, setPaidAt] = useState<string>(dayjs().format('YYYY-MM-DD'));

  useEffect(() => {
    setCurrentPage(1);
  }, [search, filterPaymentTypeId, filterFamilyId, filterMemberId, filterStatus, filterStartDate, filterEndDate]);

  useEffect(() => {
    if (hasFetched && !isLoadingPermissions && (hasReadPermission || isMainAdmin)) {
      getPayments(
        search || undefined,
        currentPage,
        limit,
        filterPaymentTypeId || undefined,
        filterFamilyId || undefined,
        filterMemberId || undefined,
        filterStatus || undefined,
        filterStartDate || undefined,
        filterEndDate || undefined
      );
    }
  }, [
    getPayments,
    search,
    currentPage,
    limit,
    filterPaymentTypeId,
    filterFamilyId,
    filterMemberId,
    filterStatus,
    filterStartDate,
    filterEndDate,
    hasReadPermission,
    isMainAdmin,
    hasFetched,
    isLoadingPermissions,
  ]);

  useEffect(() => {
    if (hasFetched && !isLoadingPermissions) {
      getPaymentTypes(undefined, 1, 100);
      getFamilies(undefined, 1, 100);
    }
  }, [hasFetched, isLoadingPermissions, getPaymentTypes, getFamilies]);

  // Fetch family members when family is selected in form
  useEffect(() => {
    if (familyId && openAddModal) {
      getFamilyMembers(familyId, undefined, 1, 100);
    }
  }, [familyId, openAddModal, getFamilyMembers]);

  // Fetch family members when filter family is selected
  useEffect(() => {
    if (filterFamilyId) {
      getFamilyMembers(filterFamilyId, undefined, 1, 100);
    }
  }, [filterFamilyId, getFamilyMembers]);

  useEffect(() => {
    setTableData(payments);
  }, [payments]);

  // Suggest amount when payment type changes (but allow editing)
  useEffect(() => {
    if (paymentTypeId && modalType === 'add' && totalAmountPaid === 0) {
      const selectedType = paymentTypes.find((pt) => pt._id === paymentTypeId);
      if (selectedType) {
        setTotalAmountPaid(selectedType.amount);
      }
    }
    // Load obligations for subscription payment types
    if (paymentTypeId && familyId) {
      const selectedType = paymentTypes.find((pt) => pt._id === paymentTypeId);
      if (selectedType && selectedType.category === 'subscription') {
        getPaymentObligations(undefined, 1, 100, paymentTypeId, familyId, undefined, undefined, undefined, undefined);
      }
    }
  }, [paymentTypeId, familyId, paymentTypes, modalType, getPaymentObligations]);

  const handleAddOrEdit = async () => {
    if (!paymentTypeId || !familyId || !totalAmountPaid || totalAmountPaid <= 0) {
      toast.error('Please fill in all required fields with valid values.', {
        duration: 2000,
        icon: '❌',
      });
      return;
    }

    const selectedType = paymentTypes.find((pt) => pt._id === paymentTypeId);
    const paymentData: any = {
      paymentTypeId,
      familyId,
      memberId: memberId || null,
      totalAmountPaid,
      paymentMethod,
      transactionId: transactionId || null,
      paidAt: paidAt || dayjs().format('YYYY-MM-DD'),
    };

    // Add allocations for subscription payment types
    if (selectedType && selectedType.category === 'subscription') {
      if (paymentAllocations.length === 0) {
        toast.error('Please allocate payment to at least one obligation');
        return;
      }
      const totalAllocated = paymentAllocations.reduce((sum, alloc) => sum + alloc.amountApplied, 0);
      if (Math.abs(totalAllocated - totalAmountPaid) > 0.01) {
        toast.error(`Total allocated amount (${totalAllocated}) must equal total amount paid (${totalAmountPaid})`);
        return;
      }
      paymentData.paymentAllocations = paymentAllocations;
    }

    if (modalType === 'add') {
      await createPayment(paymentData);
    } else {
      if (!editId) return;
      if (selectedType && selectedType.category === 'subscription') {
        paymentData.paymentAllocations = paymentAllocations;
      }
      await updatePayment(editId, paymentData);
    }

    setOpenAddModal(false);
    setPaymentTypeId('');
    setFamilyId('');
    setMemberId('');
    setTotalAmountPaid(0);
    setPaymentAllocations([]);
    setPaymentMethod('cash');
    setTransactionId('');
    setStatus('success');
    setPaidAt(dayjs().format('YYYY-MM-DD'));
    setEditId(null);
    setModalType('add');
  };

  const handleEdit = async (payment: Payment) => {
    setEditId(payment._id);
    setModalType('edit');
    setOpenAddModal(true);
    const ptId = typeof payment.paymentTypeId === 'string' ? payment.paymentTypeId : payment.paymentTypeId._id;
    setPaymentTypeId(ptId);
    const famId = typeof payment.familyId === 'string' ? payment.familyId : payment.familyId._id;
    setFamilyId(famId);
    // Fetch family members for the selected family
    if (famId) {
      await getFamilyMembers(famId, undefined, 1, 100);
    }
    const memId = payment.memberId
      ? typeof payment.memberId === 'string'
        ? payment.memberId
        : payment.memberId._id
      : '';
    setMemberId(memId);
    setTotalAmountPaid(payment.totalAmountPaid || (payment as any).amountPaid || 0);
    // Convert PaymentAllocation[] to the simpler form for state
    const allocations = (payment.paymentAllocations || []).map((alloc) => ({
      paymentObligationId:
        typeof alloc.paymentObligationId === 'string'
          ? alloc.paymentObligationId
          : alloc.paymentObligationId._id,
      amountApplied: alloc.amountApplied,
    }));
    setPaymentAllocations(allocations);
    setPaymentMethod(payment.paymentMethod);
    setTransactionId(payment.transactionId || '');
    setStatus(payment.status);
    setPaidAt(dayjs(payment.paidAt).format('YYYY-MM-DD'));
  };

  const handleDelete = () => {
    if (selectedPayment !== null) {
      deletePayment(selectedPayment);
      setOpenModal(false);
      setSelectedPayment(null);
    }
  };

  const handleExport = async () => {
    const exported = await exportPayments(
      filterPaymentTypeId || undefined,
      filterFamilyId || undefined,
      filterMemberId || undefined,
      filterStatus || undefined,
      filterStartDate || undefined,
      filterEndDate || undefined
    );

    if (exported) {
      // Convert to CSV
      const headers = Object.keys(exported[0] || {});
      const csvContent = [
        headers.join(','),
        ...exported.map((row) => headers.map((header) => JSON.stringify(row[header] || '')).join(',')),
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `payments_${dayjs().format('YYYY-MM-DD')}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Show loading while permissions are being fetched
  if (isLoadingPermissions || !hasFetched) {
    return (
      <div className="p-6">
        <div className="text-center py-8">
          <div className="text-lg text-gray-600">Loading permissions...</div>
        </div>
      </div>
    );
  }

  // Show permission denied message if no access
  if (!canAccess && !isMainAdmin) {
    return <AccessDenied resourceName="Payments" />;
  }

  return (
    <>
      <Modal
        show={openModal}
        size="md"
        position="center"
        onClose={() => {
          setOpenModal(false);
          setSelectedPayment(null);
        }}
        popup
      >
        <Modal.Header />
        <Modal.Body>
          <div className="text-center">
            <HiOutlineExclamationCircle className="mx-auto mb-4 h-14 w-14 text-gray-400 dark:text-gray-200" />
            <h3 className="mb-5 text-lg font-normal text-gray-500 dark:text-gray-400" style={{ fontFamily: 'Lato, sans-serif' }}>
              Are you sure you want to delete this payment?
            </h3>
            <div className="flex justify-center gap-4">
              <Button
                onClick={handleDelete}
                className="px-6 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium flex items-center justify-center"
                style={{ fontFamily: 'Lato, sans-serif', height: '36px' }}
              >
                {"Yes, I'm sure"}
              </Button>
              <Button
                onClick={() => {
                  setOpenModal(false);
                  setSelectedPayment(null);
                }}
                className="px-6 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium flex items-center justify-center"
                style={{ fontFamily: 'Lato, sans-serif', height: '36px' }}
              >
                No, cancel
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={openAddModal}
        size="lg"
        popup
        onClose={() => {
          setOpenAddModal(false);
          setPaymentTypeId('');
          setFamilyId('');
          setMemberId('');
          setTotalAmountPaid(0);
          setPaymentAllocations([]);
          setPaymentMethod('cash');
          setTransactionId('');
          setStatus('success');
          setPaidAt(dayjs().format('YYYY-MM-DD'));
          setEditId(null);
          setModalType('add');
        }}
      >
        <Modal.Header />
        <Modal.Body>
          <div className="space-y-6 max-h-[80vh] overflow-y-auto">
            <h3 className="text-xl font-medium text-gray-900 dark:text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
              {modalType === 'add' ? 'Add Payment' : 'Edit Payment'}
            </h3>

            <div>
              <div className="mb-2 block">
                <Label htmlFor="paymentTypeId" value="Payment Type *" style={{ fontFamily: 'Lato, sans-serif' }} />
              </div>
              <Select
                id="paymentTypeId"
                value={paymentTypeId}
                onChange={(e) => setPaymentTypeId(e.target.value)}
                style={{ fontFamily: 'Lato, sans-serif' }}
                disabled={modalType === 'edit'}
              >
                <option value="">Select Payment Type</option>
                {paymentTypes
                  .filter((pt) => pt.isActive)
                  .map((pt) => (
                    <option key={pt._id} value={pt._id}>
                      {pt.name} - {pt.currency} {pt.amount}
                    </option>
                  ))}
              </Select>
              {modalType === 'edit' && (
                <p className="text-xs text-gray-500 mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                  Payment type cannot be changed after creation
                </p>
              )}
            </div>

            <div>
              <div className="mb-2 block">
                <Label htmlFor="familyId" value="Family *" style={{ fontFamily: 'Lato, sans-serif' }} />
              </div>
              <Select
                id="familyId"
                value={familyId}
                onChange={(e) => {
                  setFamilyId(e.target.value);
                  setMemberId(''); // Reset member when family changes
                }}
                style={{ fontFamily: 'Lato, sans-serif' }}
                disabled={modalType === 'edit'}
              >
                <option value="">Select Family</option>
                {families.map((family) => (
                  <option key={family._id} value={family._id}>
                    {family.familyName}
                  </option>
                ))}
              </Select>
              {modalType === 'edit' && (
                <p className="text-xs text-gray-500 mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                  Family cannot be changed after creation
                </p>
              )}
            </div>

            <div>
              <div className="mb-2 block">
                <Label htmlFor="memberId" value="Member (Optional)" style={{ fontFamily: 'Lato, sans-serif' }} />
              </div>
              <Select
                id="memberId"
                value={memberId}
                onChange={(e) => setMemberId(e.target.value)}
                style={{ fontFamily: 'Lato, sans-serif' }}
                disabled={!familyId}
              >
                <option value="">Select Member (Optional)</option>
                {familyId
                  ? familyMembers
                      .filter((member) => {
                        const memberFamilyId = typeof member.family === 'string' ? member.family : member.family?._id || '';
                        return memberFamilyId === familyId;
                      })
                      .map((member) => (
                        <option key={member._id} value={member._id}>
                          {member.name}
                        </option>
                      ))
                  : null}
              </Select>
              {!familyId && (
                <p className="text-xs text-gray-500 mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                  Please select a family first
                </p>
              )}
            </div>

            <div>
              <div className="mb-2 block">
                <Label htmlFor="totalAmountPaid" value="Total Amount Paid *" style={{ fontFamily: 'Lato, sans-serif' }} />
              </div>
              <TextInput
                id="totalAmountPaid"
                type="number"
                placeholder="0.00"
                value={totalAmountPaid}
                onChange={(e) => {
                  const newTotal = parseFloat(e.target.value) || 0;
                  setTotalAmountPaid(newTotal);
                  // Auto-allocate if only one allocation exists
                  if (paymentAllocations.length === 1) {
                    setPaymentAllocations([{ ...paymentAllocations[0], amountApplied: newTotal }]);
                  }
                }}
                style={{ fontFamily: 'Lato, sans-serif' }}
                helperText={
                  paymentTypeId
                    ? (() => {
                        const selectedType = paymentTypes.find((pt) => pt._id === paymentTypeId);
                        return selectedType
                          ? `Expected amount: ${selectedType.currency} ${selectedType.amount.toFixed(2)} (you can enter a different amount)`
                          : '';
                      })()
                    : 'Enter the total amount paid'
                }
              />
            </div>

            {/* Payment Allocations for Subscription Payment Types */}
            {(() => {
              const selectedType = paymentTypes.find((pt) => pt._id === paymentTypeId);
              if (selectedType && selectedType.category === 'subscription' && familyId) {
                const availableObligations = obligations.filter(
                  (ob) =>
                    (typeof ob.paymentTypeId === 'string' ? ob.paymentTypeId : ob.paymentTypeId._id) === paymentTypeId &&
                    (typeof ob.familyId === 'string' ? ob.familyId : ob.familyId._id) === familyId &&
                    ob.status !== 'paid'
                );

                return (
                  <div className="border rounded-lg p-4 bg-gray-50 dark:bg-gray-800">
                    <Label value="Payment Allocations *" style={{ fontFamily: 'Lato, sans-serif', fontWeight: 'bold' }} />
                    <p className="text-xs text-gray-500 mb-3" style={{ fontFamily: 'Lato, sans-serif' }}>
                      Allocate this payment across obligations. Total allocated must equal total amount paid.
                    </p>
                    {availableObligations.length === 0 ? (
                      <div className="space-y-2">
                        <p className="text-sm text-yellow-600 mb-2" style={{ fontFamily: 'Lato, sans-serif' }}>
                          No available obligations found. Create an obligation for the current period to proceed with payment allocation.
                        </p>
                        <Button
                          size="sm"
                          onClick={async () => {
                            const selectedType = paymentTypes.find((pt) => pt._id === paymentTypeId);
                            if (!selectedType) return;
                            
                            const now = new Date();
                            let currentPeriod: string;
                            if (selectedType.frequency === 'monthly') {
                              currentPeriod = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
                            } else if (selectedType.frequency === 'yearly') {
                              currentPeriod = String(now.getFullYear());
                            } else {
                              return;
                            }
                            
                            try {
                              await createPaymentObligation({
                                paymentTypeId,
                                familyId,
                                period: currentPeriod,
                              });
                              await getPaymentObligations(undefined, 1, 100, paymentTypeId, familyId, undefined, undefined, undefined, undefined);
                              toast.success('Obligation created successfully');
                            } catch (error: any) {
                              toast.error(error?.message || 'Failed to create obligation');
                            }
                          }}
                          style={{ fontFamily: 'Lato, sans-serif' }}
                        >
                          Create Current Period Obligation
                        </Button>
                        <p className="text-xs text-gray-500 mt-2" style={{ fontFamily: 'Lato, sans-serif' }}>
                          Or visit the Outstanding Payments page to generate obligations for multiple periods.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {availableObligations.map((obligation) => {
                          const allocation = paymentAllocations.find(
                            (a) => a.paymentObligationId === obligation._id
                          ) || { paymentObligationId: obligation._id, amountApplied: 0 };
                          const remainingBalance = obligation.amountDue - obligation.amountPaid;

                          return (
                            <div key={obligation._id} className="border rounded p-3 bg-white dark:bg-gray-700">
                              <div className="flex justify-between items-center mb-2">
                                <div>
                                  <p className="font-medium" style={{ fontFamily: 'Lato, sans-serif' }}>
                                    Period: {obligation.period}
                                  </p>
                                  <p className="text-xs text-gray-500" style={{ fontFamily: 'Lato, sans-serif' }}>
                                    Due: {obligation.amountDue.toFixed(2)} | Paid: {obligation.amountPaid.toFixed(2)} |
                                    Remaining: {remainingBalance.toFixed(2)}
                                  </p>
                                </div>
                                <span
                                  className={`px-2 py-1 rounded text-xs ${
                                    obligation.status === 'paid'
                                      ? 'bg-green-100 text-green-800'
                                      : obligation.status === 'partial'
                                      ? 'bg-yellow-100 text-yellow-800'
                                      : 'bg-red-100 text-red-800'
                                  }`}
                                >
                                  {obligation.status}
                                </span>
                              </div>
                              <TextInput
                                type="number"
                                placeholder="0.00"
                                value={allocation.amountApplied}
                                onChange={(e) => {
                                  const amount = parseFloat(e.target.value) || 0;
                                  if (amount > remainingBalance) {
                                    toast.error(`Amount cannot exceed remaining balance of ${remainingBalance.toFixed(2)}`);
                                    return;
                                  }
                                  const updatedAllocations = paymentAllocations.filter(
                                    (a) => a.paymentObligationId !== obligation._id
                                  );
                                  if (amount > 0) {
                                    updatedAllocations.push({ paymentObligationId: obligation._id, amountApplied: amount });
                                  }
                                  setPaymentAllocations(updatedAllocations);
                                  // Update total if this is the only allocation
                                  if (updatedAllocations.length === 1 && updatedAllocations[0].paymentObligationId === obligation._id) {
                                    setTotalAmountPaid(amount);
                                  }
                                }}
                                min={0}
                                max={remainingBalance}
                                style={{ fontFamily: 'Lato, sans-serif' }}
                                helperText={`Max: ${remainingBalance.toFixed(2)}`}
                              />
                            </div>
                          );
                        })}
                        <div className="mt-3 p-2 bg-blue-50 dark:bg-blue-900 rounded">
                          <p className="text-sm font-medium" style={{ fontFamily: 'Lato, sans-serif' }}>
                            Total Allocated: {paymentAllocations.reduce((sum, a) => sum + a.amountApplied, 0).toFixed(2)} /{' '}
                            {totalAmountPaid.toFixed(2)}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              }
              return null;
            })()}

            <div>
              <div className="mb-2 block">
                <Label htmlFor="paymentMethod" value="Payment Method *" style={{ fontFamily: 'Lato, sans-serif' }} />
              </div>
              <Select
                id="paymentMethod"
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value as 'cash' | 'upi' | 'card' | 'bankTransfer')}
                style={{ fontFamily: 'Lato, sans-serif' }}
              >
                <option value="cash">Cash</option>
                <option value="upi">UPI</option>
                <option value="card">Card</option>
                <option value="bankTransfer">Bank Transfer</option>
              </Select>
            </div>

            <div>
              <div className="mb-2 block">
                <Label htmlFor="transactionId" value="Transaction ID (Optional)" style={{ fontFamily: 'Lato, sans-serif' }} />
              </div>
              <TextInput
                id="transactionId"
                type="text"
                placeholder="Enter transaction ID"
                value={transactionId}
                onChange={(e) => setTransactionId(e.target.value)}
                style={{ fontFamily: 'Lato, sans-serif' }}
                helperText="Required for non-cash payments"
              />
            </div>

            {modalType === 'edit' && (
              <div>
                <div className="mb-2 block">
                  <Label htmlFor="status" value="Status *" style={{ fontFamily: 'Lato, sans-serif' }} />
                </div>
                <Select
                  id="status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value as 'pending' | 'success' | 'failed')}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                >
                  <option value="pending">Pending</option>
                  <option value="success">Success</option>
                  <option value="failed">Failed</option>
                </Select>
              </div>
            )}

            <div>
              <div className="mb-2 block">
                <Label htmlFor="paidAt" value="Paid At *" style={{ fontFamily: 'Lato, sans-serif' }} />
              </div>
              <TextInput
                id="paidAt"
                type="date"
                value={paidAt}
                onChange={(e) => setPaidAt(e.target.value)}
                style={{ fontFamily: 'Lato, sans-serif' }}
              />
            </div>

            <div className="w-full">
              <Button
                onClick={handleAddOrEdit}
                disabled={
                  loading ||
                  !paymentTypeId ||
                  !familyId ||
                  !totalAmountPaid ||
                  totalAmountPaid <= 0 ||
                  (paymentTypes.find((pt) => pt._id === paymentTypeId)?.category === 'subscription' &&
                    paymentAllocations.length === 0)
                }
                className="w-full px-6 bg-[#B8860B] text-white rounded-lg hover:bg-[#9A7209] transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                style={{ fontFamily: 'Lato, sans-serif', height: '36px' }}
              >
                {loading ? 'Processing...' : modalType === 'add' ? 'Add Payment' : 'Edit Payment'}
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <PageMeta title="Payments" description="Manage payments" />
      <PageBreadcrumb pageTitle="Payments" />
      <div className="space-y-6">
        <ComponentCard title="Payments">
          <div className="flex flex-col gap-4 mb-4">
            <div className="flex flex-col md:flex-row w-full md:justify-start md:items-center gap-5">
              {(hasCreatePermission || isMainAdmin) && (
                <Button
                  onClick={() => {
                    setOpenAddModal(true);
                    setModalType('add');
                    setPaymentTypeId('');
                    setFamilyId('');
                    setMemberId('');
                    setTotalAmountPaid(0);
          setPaymentAllocations([]);
                    setPaymentMethod('cash');
                    setTransactionId('');
                    setStatus('success');
                    setPaidAt(dayjs().format('YYYY-MM-DD'));
                  }}
                  className="px-6 bg-[#B8860B] text-white rounded-lg hover:bg-[#9A7209] transition-colors font-medium flex items-center justify-center"
                  style={{ fontFamily: 'Lato, sans-serif', height: '36px' }}
                >
                  Add Payment
                </Button>
              )}
              <Button
                onClick={handleExport}
                className="px-6 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium flex items-center justify-center"
                style={{ fontFamily: 'Lato, sans-serif', height: '36px' }}
              >
                Export
              </Button>
              <div className="relative">
                <span className="absolute -translate-y-1/2 pointer-events-none left-4 top-1/2">
                  <svg
                    className="fill-gray-500 dark:fill-gray-400"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M3.04175 9.37363C3.04175 5.87693 5.87711 3.04199 9.37508 3.04199C12.8731 3.04199 15.7084 5.87693 15.7084 9.37363C15.7084 12.8703 12.8731 15.7053 9.37508 15.7053C5.87711 15.7053 3.04175 12.8703 3.04175 9.37363ZM9.37508 1.54199C5.04902 1.54199 1.54175 5.04817 1.54175 9.37363C1.54175 13.6991 5.04902 17.2053 9.37508 17.2053C11.2674 17.2053 13.003 16.5344 14.357 15.4176L17.177 18.238C17.4699 18.5309 17.9448 18.5309 18.2377 18.238C18.5306 17.9451 18.5306 17.4703 18.2377 17.1774L15.418 14.3573C16.5365 13.0033 17.2084 11.2669 17.2084 9.37363C17.2084 5.04817 13.7011 1.54199 9.37508 1.54199Z"
                      fill=""
                    />
                  </svg>
                </span>
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  type="text"
                  placeholder="Search payments..."
                  className="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-200 bg-transparent py-2.5 pl-12 pr-14 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-[#B8860B] focus:outline-none focus:ring focus:ring-[#B8860B]/10 dark:border-gray-800 dark:bg-gray-900 dark:bg-white/[0.03] dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-[#B8860B] xl:w-[430px]"
                  style={{ fontFamily: 'Lato, sans-serif' }}
                />
              </div>
            </div>

            {/* Filters */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
              <Select
                value={filterPaymentTypeId}
                onChange={(e) => setFilterPaymentTypeId(e.target.value)}
                style={{ fontFamily: 'Lato, sans-serif' }}
              >
                <option value="">All Payment Types</option>
                {paymentTypes.map((pt) => (
                  <option key={pt._id} value={pt._id}>
                    {pt.name}
                  </option>
                ))}
              </Select>

              <Select
                value={filterFamilyId}
                onChange={(e) => {
                  setFilterFamilyId(e.target.value);
                  setFilterMemberId(''); // Reset member filter when family changes
                }}
                style={{ fontFamily: 'Lato, sans-serif' }}
              >
                <option value="">All Families</option>
                {families.map((family) => (
                  <option key={family._id} value={family._id}>
                    {family.familyName}
                  </option>
                ))}
              </Select>

              <Select
                value={filterMemberId}
                onChange={(e) => setFilterMemberId(e.target.value)}
                style={{ fontFamily: 'Lato, sans-serif' }}
                disabled={!filterFamilyId}
              >
                <option value="">All Members</option>
                {filterFamilyId
                  ? familyMembers
                      .filter((member) => {
                        const memberFamilyId = typeof member.family === 'string' ? member.family : member.family?._id || '';
                        return memberFamilyId === filterFamilyId;
                      })
                      .map((member) => (
                        <option key={member._id} value={member._id}>
                          {member.name}
                        </option>
                      ))
                  : null}
              </Select>

              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                style={{ fontFamily: 'Lato, sans-serif' }}
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="success">Success</option>
                <option value="failed">Failed</option>
              </Select>

              <TextInput
                type="date"
                placeholder="Start Date"
                value={filterStartDate}
                onChange={(e) => setFilterStartDate(e.target.value)}
                style={{ fontFamily: 'Lato, sans-serif' }}
              />

              <TextInput
                type="date"
                placeholder="End Date"
                value={filterEndDate}
                onChange={(e) => setFilterEndDate(e.target.value)}
                style={{ fontFamily: 'Lato, sans-serif' }}
              />
            </div>
          </div>

          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03] mt-6">
            <div className="max-w-full overflow-x-auto">
              <div className="min-w-[1400px]">
                <Table>
                  <TableHeader className="border-b border-gray-100 dark:border-white/[0.05]">
                    <TableRow>
                      <TableCell
                        isHeader
                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        Payment Type
                      </TableCell>
                      <TableCell
                        isHeader
                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        Family
                      </TableCell>
                      <TableCell
                        isHeader
                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        Member
                      </TableCell>
                      <TableCell
                        isHeader
                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        Amount
                      </TableCell>
                      <TableCell
                        isHeader
                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        Payment Method
                      </TableCell>
                      <TableCell
                        isHeader
                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        Transaction ID
                      </TableCell>
                      <TableCell
                        isHeader
                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        Status
                      </TableCell>
                      <TableCell
                        isHeader
                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        Paid At
                      </TableCell>
                      <TableCell
                        isHeader
                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        Created At
                      </TableCell>
                      <TableCell
                        isHeader
                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHeader>

                  <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                    {tableData.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={10}
                          className="px-5 py-8 text-center text-gray-500 dark:text-gray-400"
                          style={{ fontFamily: 'Lato, sans-serif' }}
                        >
                          {loading ? 'Loading...' : 'No payments found'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      tableData.map((payment) => {
                        const paymentType = typeof payment.paymentTypeId === 'string' ? null : payment.paymentTypeId;
                        const family = typeof payment.familyId === 'string' ? null : payment.familyId;
                        const member = payment.memberId ? (typeof payment.memberId === 'string' ? null : payment.memberId) : null;
                        return (
                          <TableRow key={payment._id}>
                            <TableCell
                              className="px-4 py-3 text-gray-500 text-theme-sm dark:text-gray-400"
                              style={{ fontFamily: 'Lato, sans-serif' }}
                            >
                              {paymentType ? paymentType.name : 'N/A'}
                            </TableCell>
                            <TableCell
                              className="px-4 py-3 text-gray-500 text-theme-sm dark:text-gray-400"
                              style={{ fontFamily: 'Lato, sans-serif' }}
                            >
                              {family ? family.familyName : 'N/A'}
                            </TableCell>
                            <TableCell
                              className="px-4 py-3 text-gray-500 text-theme-sm dark:text-gray-400"
                              style={{ fontFamily: 'Lato, sans-serif' }}
                            >
                              {member ? member.name : '-'}
                            </TableCell>
                            <TableCell
                              className="px-4 py-3 text-gray-500 text-theme-sm dark:text-gray-400"
                              style={{ fontFamily: 'Lato, sans-serif' }}
                            >
                              {paymentType ? paymentType.currency : 'INR'}{' '}
                              {(payment.totalAmountPaid || (payment as any).amountPaid || 0).toFixed(2)}
                            </TableCell>
                            <TableCell
                              className="px-4 py-3 text-gray-500 text-theme-sm dark:text-gray-400"
                              style={{ fontFamily: 'Lato, sans-serif' }}
                            >
                              {payment.paymentMethod.charAt(0).toUpperCase() + payment.paymentMethod.slice(1)}
                            </TableCell>
                            <TableCell
                              className="px-4 py-3 text-gray-500 text-theme-sm dark:text-gray-400"
                              style={{ fontFamily: 'Lato, sans-serif' }}
                            >
                              {payment.transactionId || '-'}
                            </TableCell>
                            <TableCell
                              className="px-4 py-3 text-gray-500 text-theme-sm dark:text-gray-400"
                              style={{ fontFamily: 'Lato, sans-serif' }}
                            >
                              <span
                                className={`px-2 py-1 rounded text-xs font-semibold ${
                                  payment.status === 'success'
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                    : payment.status === 'pending'
                                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                    : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                }`}
                              >
                                {payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}
                              </span>
                            </TableCell>
                            <TableCell
                              className="px-4 py-3 text-gray-500 text-theme-sm dark:text-gray-400"
                              style={{ fontFamily: 'Lato, sans-serif' }}
                            >
                              {dayjs(payment.paidAt).format('DD MMM YYYY')}
                            </TableCell>
                            <TableCell
                              className="px-4 py-3 text-gray-500 text-theme-sm dark:text-gray-400"
                              style={{ fontFamily: 'Lato, sans-serif' }}
                            >
                              {dayjs(payment.createdAt).format('DD MMM YYYY')}
                            </TableCell>
                            <TableCell className="px-4 py-3 text-gray-500 text-theme-sm dark:text-gray-400">
                              <div onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                                <Popover
                                  placement="top"
                                  aria-labelledby="default-popover"
                                  content={
                                    <div className="py-2 px-3">
                                      {payment.status !== 'success' && (hasDeletePermission || isMainAdmin) && (
                                        <div
                                          className="flex items-center gap-3 px-3 py-2 font-medium text-gray-700 rounded-lg group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300 cursor-pointer"
                                          onClick={() => {
                                            setSelectedPayment(payment._id);
                                            setOpenModal(true);
                                          }}
                                          style={{ fontFamily: 'Lato, sans-serif' }}
                                        >
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            className="lucide lucide-trash-2"
                                          >
                                            <path d="M3 6h18" />
                                            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                                            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                                            <line x1="10" x2="10" y1="11" y2="17" />
                                            <line x1="14" x2="14" y1="11" y2="17" />
                                          </svg>
                                          Delete
                                        </div>
                                      )}

                                      {payment.status !== 'success' && (hasUpdatePermission || isMainAdmin) && (
                                        <div
                                          className="flex items-center gap-3 px-3 py-2 font-medium text-gray-700 rounded-lg group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300 cursor-pointer"
                                          onClick={() => handleEdit(payment)}
                                          style={{ fontFamily: 'Lato, sans-serif' }}
                                        >
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            className="lucide lucide-pencil"
                                          >
                                            <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5 5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
                                            <path d="m15 5 4 4" />
                                          </svg>
                                          Edit
                                        </div>
                                      )}
                                    </div>
                                  }
                                >
                                  <button
                                    className="flex items-center justify-center w-10 h-10 text-gray-700 rounded-lg z-99999 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800 transition-colors"
                                    style={{ fontFamily: 'Lato, sans-serif' }}
                                  >
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M5.99902 10.4951C6.82745 10.4951 7.49902 11.1667 7.49902 11.9951V12.0051C7.49902 12.8335 6.82745 13.5051 5.99902 13.5051C5.1706 13.5051 4.49902 12.8335 4.49902 12.0051V11.9951C4.49902 11.1667 5.1706 10.4951 5.99902 10.4951ZM17.999 10.4951C18.8275 10.4951 19.499 11.1667 19.499 11.9951V12.0051C19.499 12.8335 18.8275 13.5051 17.999 13.5051C17.1706 13.5051 16.499 12.8335 16.499 12.0051V11.9951C16.499 11.1667 17.1706 10.4951 17.999 10.4951ZM13.499 11.9951C13.499 11.1667 12.8275 10.4951 11.999 10.4951C11.1706 10.4951 10.499 11.1667 10.499 11.9951V12.0051C10.499 12.8335 11.1706 13.5051 11.999 13.5051C12.8275 13.5051 13.499 12.8335 13.499 12.0051V11.9951Z"
                                        fill="currentColor"
                                      />
                                    </svg>
                                  </button>
                                </Popover>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 px-4 py-3 border-t border-gray-200 dark:border-white/[0.05]">
              <div className="text-sm text-gray-500 dark:text-gray-400" style={{ fontFamily: 'Lato, sans-serif' }}>
                Showing {(pagination.page - 1) * pagination.limit + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                {pagination.total} results
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  color="light"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={!pagination.hasPrevPage || loading}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                >
                  Previous
                </Button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (pagination.totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.page <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.page >= pagination.totalPages - 2) {
                      pageNum = pagination.totalPages - 4 + i;
                    } else {
                      pageNum = pagination.page - 2 + i;
                    }
                    return (
                      <Button
                        key={pageNum}
                        size="sm"
                        color={pagination.page === pageNum ? 'warning' : 'light'}
                        onClick={() => setCurrentPage(pageNum)}
                        disabled={loading}
                        style={{ fontFamily: 'Lato, sans-serif', minWidth: '40px' }}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                <Button
                  size="sm"
                  color="light"
                  onClick={() => setCurrentPage((p) => Math.min(pagination.totalPages, p + 1))}
                  disabled={!pagination.hasNextPage || loading}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </ComponentCard>
      </div>
    </>
  );
}
